import { motion } from "motion/react"
import { Check, Bell, Settings2, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/Button"
import { ChitBadge } from "@/components/ui/ChitBadge"
import { cn } from "@/utils/Functions"
import type { Integration } from "@/types/integrations.types"
import type { NylasConnection } from "@equaliq/api-model"
interface IntegrationCardProps {
  integration: Integration
  index: number
  connection?: NylasConnection
  connecting?: boolean
  onConnect: (id: string) => void
  onManage: (id: string) => void
  onNotify: (id: string) => void
}

export const IntegrationCard = ({
  integration,
  index,
  connection,
  connecting = false,
  onConnect,
  onManage,
  onNotify,
}: IntegrationCardProps) => {
  const Icon = integration.icon

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.2, delay: index * 0.03 }}
      className={cn(
        "group relative bg-card border rounded-2xl transition-colors hover:bg-muted/50",
        integration.status === "connected"
          ? "border-green-500/30 bg-border/30"
          : "border-border"
      )}
    >
      <div className="p-4">
        <div className="flex items-start justify-between mb-4">
          <div
            className={cn(
              "flex items-center justify-center size-11 rounded-xl bg-gradient-to-br border",
              integration.accentFrom,
              integration.accentTo,
              integration.accentBorder
            )}
          >
            <Icon className="size-5 text-foreground/80" />
          </div>

          <ChitBadge
            variant={integration.scope === "personal" ? "info" : "default"}
            size="sm"
          >
            {integration.scope === "personal" ? "Personal" : "Org"}
          </ChitBadge>
        </div>

        <h3 className="text-foreground font-medium mb-1 truncate">
          {integration.name}
        </h3>
        <p className="text-muted-foreground text-sm mb-1 line-clamp-2 min-h-10">
          {integration.description}
        </p>

        {integration.provider && (
          <p className="text-muted-foreground/50 text-xs mb-3">
            {integration.provider}
          </p>
        )}
        {!integration.provider && <div className="mb-3" />}
      </div>

      <div className="border-t border-border p-4 flex items-center justify-between">
        {integration.status === "connected" && (
          <>
            <div className="flex items-center gap-1.5">
              <Check className="size-3.5 text-green-400" />
              <span className="text-green-400 text-xs font-medium">Connected</span>
            </div>
            <Button
              onClick={() => onManage(integration.id)}
              variant="outline"
              size="sm"
            >
              <Settings2 className="size-4" />
              <span className="text-xs">Manage</span>
            </Button>
          </>
        )}

        {integration.status === "available" && (
          <>
            <span />
            <Button
              size="sm"
              className="gap-2"
              onClick={() => onConnect(integration.id)}
              disabled={connecting}
            >
              {connecting ? (
                <>
                  <Loader2 className="size-3.5 animate-spin" />
                  Connecting...
                </>
              ) : (
                "Connect"
              )}
            </Button>
          </>
        )}

        {integration.status === "coming_soon" && (
          <>
            <ChitBadge variant="default" size="sm">
              Coming Soon
            </ChitBadge>
            <button
              onClick={() => onNotify(integration.id)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <Bell className="size-3" />
              Notify Me
            </button>
          </>
        )}
      </div>
    </motion.div>
  )
}
